import type { GeoFix, GeolocationPort } from './types';

/**
 * Browser Geolocation. Capacitor swap: @capacitor/geolocation.
 *
 * Why this is a watch and not getCurrentPosition:
 *
 * `getCurrentPosition` with `maximumAge: 0` still hands back whatever the OS
 * can produce FIRST, which on a phone that has been sitting in a pocket is a
 * cell/wifi estimate, not GPS. Field data showed exactly that — first press
 * reported 38-43 m accuracy (and once 2000 m, a pure tower fix), a minute later
 * the same spot reported 5 m. With a 30 m geofence the coarse reading lands the
 * driver outside the fence and the completion is refused, even though they are
 * standing at the door.
 *
 * So: open a watch, throw away readings the OS timestamped before we asked,
 * and keep taking updates until accuracy is good enough or time runs out —
 * returning the best fix seen either way. Typically resolves in a couple of
 * seconds once the chip is warm, which is what `warmUp()` is for.
 */

let lastKnown: GeoFix | null = null;

/** Anything worse than this is not worth waiting longer for. */
const TARGET_ACCURACY_M = 25;
/** The server refuses a completion above this, so it is the point of no value. */
export const SERVER_MAX_ACCURACY_M = 100;

const toFix = (pos: GeolocationPosition): GeoFix => ({
  lat: pos.coords.latitude,
  lng: pos.coords.longitude,
  accuracyM: pos.coords.accuracy,
  capturedAt: pos.timestamp,
});

export const geolocation: GeolocationPort = {
  getFreshFix(timeoutMs = 20_000, targetAccuracyM = TARGET_ACCURACY_M): Promise<GeoFix> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('geolocation_unsupported'));
        return;
      }

      const askedAt = Date.now();
      let best: GeoFix | null = null;
      let watchId: number | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;

      const done = (fix: GeoFix | null, err?: Error) => {
        if (watchId !== null) navigator.geolocation.clearWatch(watchId);
        if (timer !== null) clearTimeout(timer);
        watchId = null; timer = null;
        if (fix) { lastKnown = fix; resolve(fix); } else { reject(err); }
      };

      timer = setTimeout(() => {
        // Out of time: the best reading beats no reading. The caller decides
        // whether its accuracy is good enough to act on.
        done(best, new Error('geolocation_timeout'));
      }, timeoutMs);

      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          // A fix the OS made before we asked is a cached one. Allow a small
          // slack so a genuinely fresh reading is not discarded on a slow clock.
          if (pos.timestamp < askedAt - 3_000) return;

          const fix = toFix(pos);
          lastKnown = fix;
          if (!best || fix.accuracyM < best.accuracyM) best = fix;
          if (fix.accuracyM <= targetAccuracyM) done(fix);
        },
        (err) => {
          // A denial is final; a transient error still leaves the timeout to
          // work with whatever the watch has already produced.
          if (err.code === err.PERMISSION_DENIED) {
            done(null, new Error('geolocation_denied'));
          } else if (!best) {
            done(null, new Error(
              err.code === err.TIMEOUT ? 'geolocation_timeout' : 'geolocation_unavailable'));
          }
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: timeoutMs },
      );
    });
  },

  getLastKnownFix(maxAgeMs = 90_000): GeoFix | null {
    if (!lastKnown) return null;
    // Older than the caller will accept is the same as having nothing: better
    // to ask again than to answer with the wrong place.
    if (Date.now() - lastKnown.capturedAt > maxAgeMs) return null;
    return lastKnown;
  },

  /**
   * Start the GPS receiver early so it is already locked when the driver taps.
   * Called when a stop opens: the chip needs several seconds from cold, and
   * that wait used to land on the driver at the worst moment.
   */
  warmUp(): () => void {
    if (!('geolocation' in navigator)) return () => {};
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const fix = toFix(pos);
        if (!lastKnown || fix.accuracyM <= lastKnown.accuracyM
            || fix.capturedAt > lastKnown.capturedAt) {
          lastKnown = fix;
        }
      },
      () => { /* warm-up is best effort; the real request reports failures */ },
      { enableHighAccuracy: true, maximumAge: 0 },
    );
    return () => navigator.geolocation.clearWatch(id);
  },
};
